/**
 * <s-card-gallery> — Swipeable product-card photos.
 * Children: [data-card-gallery-track] (native overflow-x scroller with snap),
 * [data-card-gallery-prev]/[-next]
 * (desktop hover arrows). Swiping uses the browser's own scrolling so vertical
 * swipes still scroll the page. Arrow taps never follow the card link.
 * On mouse hover over the first photo, sets [data-hover-swap] so the second photo
 * (.card-hover-image, snippets/product-card-hover-image.liquid) fades in; the
 * arrows then count that photo as the one showing.
 */
class SCardGallery extends HTMLElement {
  #controller = null;

  connectedCallback() {
    const track = this.querySelector('[data-card-gallery-track]');
    if (!track || track.children.length < 2) return;

    const prev = this.querySelector('[data-card-gallery-prev]');
    const next = this.querySelector('[data-card-gallery-next]');
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const dir = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const count = () => [...track.children].filter((el) => !el.hidden).length;
    const swapping = () => this.hasAttribute('data-hover-swap');

    const index = () => Math.round(Math.abs(track.scrollLeft) / Math.max(track.clientWidth, 1));
    const shown = () => (swapping() ? 1 : Math.min(index(), count() - 1));
    const update = () => {
      const i = shown();

      prev?.toggleAttribute('data-disabled', i <= 0);
      next?.toggleAttribute('data-disabled', i >= count() - 1);
    };
    const go = (step) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      const target = Math.max(0, Math.min(count() - 1, shown() + step));
      this.removeAttribute('data-hover-swap');
      if (target !== index()) track.scrollTo({ left: target * track.clientWidth * dir, behavior });
      update();
    };

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || index() !== 0) return;
      if (!this.querySelector('.card-hover-image:not([hidden])')) return;
      this.setAttribute('data-hover-swap', '');
      update();
    }, { signal });
    this.addEventListener('pointerleave', () => {
      this.removeAttribute('data-hover-swap');
      update();
    }, { signal });

    prev?.addEventListener('click', go(-1), { signal });
    next?.addEventListener('click', go(1), { signal });
    track.addEventListener('scroll', () => {
      if (index() > 0) this.removeAttribute('data-hover-swap');
      update();
    }, { signal, passive: true });
    window.addEventListener('resize', update, { signal });
    this.refresh = update;
    update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('s-card-gallery')) customElements.define('s-card-gallery', SCardGallery);
